import i18n from '@/i18n';
import { sheinShopCapabilities } from '@/shein-platform/utils/sheinShopCapabilities';
import { tiktokCountryLabel } from '@/utils/platformShopCatalog';

function shopText(key, options) {
  return i18n.t(key, { ns: 'workbench', ...options });
}

export function is1688Platform(platformCode) {
  return String(platformCode || '').toLowerCase() === '1688';
}

export function isSheinPlatform(platformCode) {
  return String(platformCode || '').toUpperCase() === 'SHEIN';
}

export function isTemuPlatform(platformCode) {
  return String(platformCode || '').toUpperCase() === 'TEMU';
}

export function isTiktokPlatform(platformCode) {
  return String(platformCode || '').toUpperCase() === 'TIKTOK';
}

/** 半托管/自运营：店铺名称由 SHEIN query-store-info 返回，无需手填 */
export function sheinProvidesStoreNameFromPlatform(shopTypeOrShop) {
  return sheinShopCapabilities(shopTypeOrShop).storeNameStatusAvailable === true;
}

/**
 * SHEIN 店铺类型 → 后端店铺模式 key（与 {@code SheinShopMode.configKey()} 对齐：semi/self/full）。
 *
 * <p>测试秘钥绑定的入口按模式判断：后端能按模式解析出「测试域 + 可用应用凭证」时才显示，
 * 所以前端必须用同一套 key 去查 `integrationConfig.sheinTestKeyBindModes`。
 * 无法识别时返回 null（调用方回退到平台级开关）。
 */
export function sheinShopModeConfigKey(shopTypeOrShop) {
  const raw = typeof shopTypeOrShop === 'string' ? shopTypeOrShop : shopTypeOrShop?.shopType;
  const type = String(raw || '').trim();
  if (!type) return null;
  const upper = type.toUpperCase();
  if (type.includes('半托') || upper === 'SEMI' || upper === 'SEMI_HOSTED' || upper === 'BAN_TUO') {
    return 'semi';
  }
  if (
    type.includes('自运营')
    || type.includes('自主运营')
    || upper === 'SELF'
    || upper === 'SELF_OPERATED'
    || upper === 'ZI_YUN_YING'
  ) {
    return 'self';
  }
  if (
    type.includes('全托')
    || upper === 'FULL'
    || upper === 'FULL_HOSTED'
    || upper === 'QUAN_TUO'
    || upper === 'OBM'
    || upper === 'POP'
  ) {
    return 'full';
  }
  return null;
}

export function formatShopListName(shopName, platformCode) {
  const name = String(shopName || '').trim();
  const code = String(platformCode || '').toUpperCase();
  if (!name || name === '-' || name === 'PENDING_TEMU_SHOP' || name === 'PENDING_TIKTOK_SHOP') {
    if (code === 'TEMU' || code === 'TIKTOK' || name === '-' || name === 'PENDING_TEMU_SHOP' || name === 'PENDING_TIKTOK_SHOP') {
      return '-';
    }
    return i18n.t('workbench:shop.pendingName');
  }
  return name;
}

function splitJoinedLabels(values) {
  const raw = Array.isArray(values) ? values : (values == null || values === '' ? [] : [values]);
  const out = [];
  raw.forEach((item) => {
    String(item || '').split(/[、]/).forEach((part) => {
      const label = part.trim();
      if (label && !out.includes(label)) out.push(label);
    });
  });
  return out;
}

/** 经营站点标签列表。TEMU 固定「全站点」。TikTok 无站点时回落国家。 */
export function listOperatingSiteLabels(sites, platformCode, shop) {
  if (isTemuPlatform(platformCode)) return [i18n.t('workbench:shop.allSites')];
  const list = splitJoinedLabels(sites);
  if (list.length) return list;
  if (isTiktokPlatform(platformCode || shop?.platformCode) && shop?.tiktokCountry) {
    const code = String(shop.tiktokCountry || '').trim().toUpperCase();
    const label = code
      ? i18n.t(`workbench:shop.country.${code}`, {
        defaultValue: tiktokCountryLabel(code),
      })
      : '';
    return label ? [label] : [];
  }
  return [];
}

/** 经营站点：多个站点用「、」拼接。TEMU 固定展示「全站点」。TikTok 无站点时回落国家。 */
export function formatOperatingSites(sites, platformCode, shop) {
  const labels = listOperatingSiteLabels(sites, platformCode, shop);
  return labels.length ? labels.join('、') : '—';
}

/** 可发类目展示列表。TEMU 固定为「全类目」。 */
export function formatPublishableRootCategories(shop) {
  if (isTemuPlatform(shop?.platformCode)) return [i18n.t('workbench:shop.allCategories')];
  const list = Array.isArray(shop?.publishableRootCategories) ? shop.publishableRootCategories : [];
  return list.map((name) => String(name || '').trim()).filter(Boolean);
}

/** 店铺是否包含指定经营站点（精确匹配站点标签）。TEMU 视为全站点，始终匹配。 */
export function shopHasOperatingSite(shop, siteLabel) {
  const target = String(siteLabel || '').trim();
  if (!target) return true;
  if (isTemuPlatform(shop?.platformCode)) return true;
  const list = Array.isArray(shop?.operatingSites) ? shop.operatingSites : [];
  return list.some((site) => String(site || '').trim() === target);
}

export function isOAuthReadyForPlatform(platformCode, integrationConfig) {
  const code = String(platformCode || '').toUpperCase();
  if (code === '1688') {
    return integrationConfig?.oauth1688Configured === true;
  }
  if (code === 'SHEIN') {
    return integrationConfig?.oauthSheinConfigured === true;
  }
  if (code === 'TEMU') {
    return integrationConfig?.oauthTemuConfigured === true;
  }
  if (code === 'TIKTOK') {
    return integrationConfig?.oauthTiktokConfigured === true;
  }
  return false;
}

export function usesMockAuthByDefault(platformCode, integrationConfig) {
  if (is1688Platform(platformCode)) {
    return !integrationConfig?.oauth1688Configured;
  }
  if (isSheinPlatform(platformCode)) {
    return !integrationConfig?.oauthSheinConfigured;
  }
  if (isTemuPlatform(platformCode)) {
    return !integrationConfig?.oauthTemuConfigured;
  }
  if (isTiktokPlatform(platformCode)) {
    return !integrationConfig?.oauthTiktokConfigured;
  }
  return true;
}

export function authStatusClass(status, mockBinding) {
  if (mockBinding) return 'processing';
  if (status === 'connected') return 'success';
  if (status === 'expired') return 'failed';
  return 'waiting';
}

export function authLabel(status, mockBinding) {
  if (mockBinding) return shopText('shop.mockBound');
  if (status === 'connected') return shopText('shop.authConnected');
  if (status === 'expired') return shopText('shop.authExpired');
  return shopText('shop.authDisconnected');
}

/** 店铺 Token 预计过期时间（列表列）；无则 —；SHEIN 无过期时间默认长期有效 */
export function formatExpectedTokenExpiry(shop) {
  const formatted = formatTokenExpiry(shop?.tokenExpiresAt);
  if (formatted) return formatted;
  if (isSheinPlatform(shop?.platformCode)) return i18n.t('workbench:shop.longValid');
  return '—';
}

/** 过期时间列样式：已过期 / 有效 / 未知；SHEIN 无过期时间视为长期有效 */
export function tokenExpiryStatusClass(shop) {
  const raw = shop?.tokenExpiresAt;
  if (!raw) return isSheinPlatform(shop?.platformCode) ? 'success' : 'waiting';
  const t = Date.parse(String(raw));
  if (!Number.isFinite(t)) return isSheinPlatform(shop?.platformCode) ? 'success' : 'waiting';
  return t <= Date.now() ? 'failed' : 'success';
}

/** SHEIN 按同步结果展示；TEMU 固定「可发品」；其余平台未同步时返回 — */
export function formatCanPublishProduct(shop) {
  if (isTemuPlatform(shop?.platformCode)) return i18n.t('workbench:shop.canPublish');
  if (!isSheinPlatform(shop?.platformCode)) return '—';
  if (shop?.canPublishProduct === true) return i18n.t('workbench:shop.canPublish');
  if (shop?.canPublishProduct === false) return i18n.t('workbench:shop.cannotPublish');
  return '—';
}

export function canPublishStatusClass(shop) {
  if (isTemuPlatform(shop?.platformCode)) return 'success';
  if (!isSheinPlatform(shop?.platformCode)) return 'waiting';
  if (shop?.canPublishProduct === true) return 'success';
  if (shop?.canPublishProduct === false) return 'failed';
  return 'waiting';
}

/** SHEIN 可发品额度（复用店铺信息 storeProductQuota.availableLimit） */
export function formatAvailablePublishQuota(shop) {
  if (!isSheinPlatform(shop?.platformCode)) return '—';
  const n = shop?.availablePublishQuota;
  if (n == null || n === '') return '—';
  const num = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString('zh-CN');
}

/** 与 authLabel 展示一致，供列表授权状态筛选 */
export function shopAuthFilterKey(shop) {
  if (shop?.mockBinding) return 'mock';
  const status = shop?.authStatus;
  if (status === 'connected') return 'connected';
  if (status === 'expired') return 'expired';
  return 'disconnected';
}

/** 运营文本匹配（兼容旧筛选）；现店铺页已改用下拉按 assignedOperatorId 精确筛选 */
export function shopOwnerSearchHaystack(ownerUserId, ownerNameByUserId) {
  if (ownerUserId == null || ownerUserId === '') return '';
  const id = String(ownerUserId);
  const base = ownerNameByUserId?.[id] || `成员 ${id}`;
  return `${base} ${id}`.toLowerCase();
}

export function formatTokenExpiry(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString('zh-CN', { hour12: false });
  } catch {
    return null;
  }
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatYmd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function formatShopDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return formatYmd(d);
  } catch {
    return '—';
  }
}

export function formatShopDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return `${formatYmd(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
  } catch {
    return '—';
  }
}
